import React, { useEffect, useState } from 'react';
import PageHeader from '@/components/PageHeader';
import AdminBillingNav from '@/components/adminBilling/AdminBillingNav';
import StatCard from '@/components/adminBilling/StatCard';
import SimpleBarChart from '@/components/adminBilling/SimpleBarChart';
import { adminBillingService, formatCents, type AdminBillingRevenue } from '@/api/services/adminBilling';
import { formatApiError } from '@/utils/formatApiError';
import { Navigate } from 'react-router-dom';
import { useMe } from '@/context/MeContext';

export default function AdminBillingRevenuePage() {
  const { me } = useMe();
  const [data, setData] = useState<AdminBillingRevenue | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminBillingService.revenue().then(setData).catch((e) => setError(formatApiError(e)));
  }, []);

  if (!me?.permissions.canViewAdminRevenue) {
    return <Navigate to="/admin/billing" replace />;
  }

  return (
    <div className="admin-billing-page">
      <AdminBillingNav />
      <PageHeader title="Revenue" subtitle="MRR, ARR, and revenue breakdown." />
      {error ? <div className="alert alert-error">{error}</div> : null}
      {!data ? <div className="panel">Loading revenue…</div> : (
        <>
          <div className="admin-billing-stat-grid">
            <StatCard label="MRR" value={formatCents(data.mrrCents)} />
            <StatCard label="ARR" value={formatCents(data.arrCents)} />
            <StatCard label="Refunds this month" value={formatCents(data.refundsThisMonthCents)} />
            <StatCard label="Conversion rate" value={`${data.conversionRatePercent}%`} />
          </div>
          <div className="admin-billing-charts-row">
            <SimpleBarChart
              title="Revenue by month"
              items={data.revenueByMonth as unknown as Record<string, unknown>[]}
              valueKey="revenueCents"
              labelKey="month"
            />
            <SimpleBarChart
              title="MRR by plan"
              items={data.revenueByPlan as unknown as Record<string, unknown>[]}
              valueKey="mrrCents"
              labelKey="planName"
            />
          </div>
          <SimpleBarChart
            title="Plan distribution"
            items={data.planDistribution as unknown as Record<string, unknown>[]}
            labelKey="planName"
          />
        </>
      )}
    </div>
  );
}
